import React, { useEffect, useState, useRef } from 'react';
import {
  Camera, Save, CheckCircle, AlertCircle, Building2,
  Mail, Globe, MapPin, FileText, Languages, ChevronDown
} from 'lucide-react';
import { fetchProfile, updateProfile, uploadLogo } from '../../api/supplierApi';
import { PhoneInput } from '../../components/PhoneInput';
import { UN_COUNTRIES_EN } from '../../countries';
import { isValidUrl, isValidPhoneDigits, normalizeUrl } from '../../lib/validation';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// ── Progress calculation ──────────────────────────────────────────────────────
const PROGRESS_FIELDS = [
  { key: 'company_name',        weight: 10 },
  { key: 'contact_person_name', weight: 10 },
  { key: 'email',               weight: 10 },
  { key: 'country',             weight: 10 },
  { key: 'phone',               weight: 10 },
  { key: 'city',                weight: 10 },
  { key: 'address',             weight: 10 },
  { key: 'short_description',   weight: 10 },
  { key: 'full_description',    weight:  5 },
  { key: 'logo_url',            weight:  5 },
  { key: 'website',             weight:  5 },
  { key: 'supported_languages', weight:  5 },  // array
];

function calcProgress(f) {
  if (!f) return 0;
  return PROGRESS_FIELDS.reduce((acc, { key, weight }) => {
    const v = f[key];
    const filled = Array.isArray(v) ? v.length > 0 : !!v;
    return acc + (filled ? weight : 0);
  }, 0);
}

const FIELD = ({ label, icon: Icon, children, span2 = false }) => (
  <div className={`space-y-1.5 ${span2 ? 'col-span-2' : ''}`}>
    <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1.5">
      {Icon && <Icon className="h-3.5 w-3.5" />}{label}
    </Label>
    {children}
  </div>
);

const SELECT_CLS = "w-full rounded-xl border border-gray-200 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-300";

// ── Parse phone stored as "+90 5551234567" ────────────────────────────────────
function parsePhone(rawPhone) {
  if (!rawPhone) return { code: '+90', number: '' };
  const trimmed = rawPhone.trim();
  if (trimmed.startsWith('+')) {
    const spaceIdx = trimmed.indexOf(' ');
    if (spaceIdx > 0) {
      return { code: trimmed.slice(0, spaceIdx), number: trimmed.slice(spaceIdx + 1) };
    }
  }
  return { code: '+90', number: trimmed };
}

export default function ProfileSection({ tr, onProfileUpdate }) {
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState({});
  const [phoneCode, setPhoneCode] = useState('+90');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [websiteTouched, setWebsiteTouched] = useState(false);
  const [phoneTouched, setPhoneTouched] = useState(false);
  const logoRef = useRef(null);

  const websiteValid = !form.website || isValidUrl(form.website);
  const phoneValid   = !phoneNumber   || isValidPhoneDigits(phoneNumber, { min: 7, max: 15 });

  useEffect(() => {
    fetchProfile()
      .then(p => {
        setProfile(p);
        setForm(p);
        const { code, number } = parsePhone(p.phone);
        setPhoneCode(code);
        setPhoneNumber(number);
      })
      .catch(() => toast.error(tr ? 'Profil yüklenemedi.' : 'Failed to load profile.'))
      .finally(() => setLoading(false));
  }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const progress = calcProgress({ ...form, phone: phoneNumber ? `${phoneCode} ${phoneNumber}` : '' });

  const progressColor = progress >= 80 ? '#22c55e' : progress >= 50 ? '#f59e0b' : '#ef4444';
  const progressLabel = progress >= 80 ? (tr ? 'Güçlü Profil' : 'Strong Profile')
    : progress >= 50 ? (tr ? 'Orta Profil' : 'Medium Profile')
    : (tr ? 'Eksik Profil' : 'Incomplete Profile');

  const handleSave = async () => {
    if (form.website && !websiteValid) {
      setWebsiteTouched(true);
      toast.error(tr ? 'Geçerli bir web sitesi giriniz.' : 'Please enter a valid website.');
      return;
    }
    if (phoneNumber && !phoneValid) {
      setPhoneTouched(true);
      toast.error(tr ? 'Telefon 7-15 rakam içermelidir.' : 'Phone must be 7-15 digits.');
      return;
    }
    setSaving(true);
    try {
      const combinedPhone = phoneNumber ? `${phoneCode} ${phoneNumber}` : (form.phone || null);
      const updated = await updateProfile({
        company_name: form.company_name,
        short_description: form.short_description,
        full_description: form.full_description,
        contact_person_name: form.contact_person_name,
        phone: combinedPhone,
        website: form.website ? normalizeUrl(form.website) : null,
        country: form.country,
        city: form.city,
        address: form.address,
        zip_code: form.zip_code,
        trade_license_number: form.is_turkish_company ? null : form.trade_license_number,
        tax_or_registration_number: form.is_turkish_company ? null : form.tax_or_registration_number,
        is_turkish_company: form.is_turkish_company || false,
        official_company_title: form.official_company_title,
        tax_office: form.tax_office,
        tax_no: form.tax_no,
        trade_registry_no: form.trade_registry_no,
        supported_languages: form.supported_languages || [],
        service_regions: form.service_regions || [],
      });
      setProfile(updated);
      setForm(updated);
      const { code, number } = parsePhone(updated.phone);
      setPhoneCode(code);
      setPhoneNumber(number);
      if (onProfileUpdate) onProfileUpdate(updated);
      toast.success(tr ? 'Profil güncellendi.' : 'Profile updated successfully.');
    } catch (e) {
      toast.error(e.response?.data?.detail || (tr ? 'Hata oluştu.' : 'An error occurred.'));
    } finally { setSaving(false); }
  };

  const handleLogoUpload = async (file) => {
    setLogoUploading(true);
    try {
      const { logo_url } = await uploadLogo(file);
      setProfile(p => ({ ...p, logo_url }));
      setForm(f => ({ ...f, logo_url }));
      if (onProfileUpdate) onProfileUpdate({ ...form, logo_url });
      toast.success(tr ? 'Logo yüklendi.' : 'Logo uploaded.');
    } catch (e) {
      toast.error(e.response?.data?.detail || (tr ? 'Logo yüklenemedi.' : 'Failed to upload logo.'));
    } finally { setLogoUploading(false); }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin h-10 w-10 border-4 border-amber-500 border-t-transparent rounded-full" />
    </div>
  );

  const logoSrc = form.logo_url ? `${API_BASE}${form.logo_url}` : null;
  const isTurkish = !!form.is_turkish_company;

  return (
    <div className="space-y-6 max-w-3xl">

      {/* ── Header ── */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">{tr ? 'Profil' : 'Profile'}</h2>
          <p className="text-sm text-gray-500 mt-0.5">{tr ? 'Firma bilgilerinizi yönetin.' : 'Manage your company information.'}</p>
        </div>
        {/* Turkish Company Toggle */}
        <label className="flex items-center gap-2 cursor-pointer bg-red-50 border border-red-200 rounded-xl px-3 py-2 select-none shrink-0" data-testid="turkish-company-toggle">
          <input
            type="checkbox"
            checked={isTurkish}
            onChange={e => set('is_turkish_company', e.target.checked)}
            className="h-4 w-4 rounded accent-red-600"
          />
          <span className="text-sm font-semibold text-red-700">{tr ? 'Türk Şirketi' : 'Turkish Company'}</span>
          <span className="text-lg">🇹🇷</span>
        </label>
      </div>

      {/* ── Progress Bar ── */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
        <div className="flex items-center justify-between mb-2">
          <p className="text-sm font-semibold text-gray-700">{tr ? 'Profil Tamamlanma' : 'Profile Completion'}</p>
          <div className="flex items-center gap-2">
            <span className="text-2xl font-bold" style={{ color: progressColor }}>{progress}%</span>
            <span className="text-xs font-medium px-2 py-0.5 rounded-full" style={{ backgroundColor: progressColor + '20', color: progressColor }}>{progressLabel}</span>
          </div>
        </div>
        <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-700"
            style={{ width: `${progress}%`, backgroundColor: progressColor }}
          />
        </div>
        {progress < 100 && (
          <p className="text-xs text-gray-400 mt-2">
            {tr
              ? `${PROGRESS_FIELDS.filter(f => { const v = form[f.key]; return Array.isArray(v) ? v.length === 0 : !v; }).map(f => f.key.replace(/_/g, ' ')).slice(0,3).join(', ')} alanlarını doldurun.`
              : `Complete: ${PROGRESS_FIELDS.filter(f => { const v = form[f.key]; return Array.isArray(v) ? v.length === 0 : !v; }).map(f => f.key.replace(/_/g, ' ')).slice(0,3).join(', ')}.`}
          </p>
        )}
      </div>

      {/* ── Logo ── */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
        <p className="text-sm font-semibold text-gray-700 mb-4">{tr ? 'Firma Logosu' : 'Company Logo'}</p>
        <div className="flex items-center gap-5">
          <div className="relative">
            {logoSrc
              ? <img src={logoSrc} alt="Logo" className="h-20 w-20 rounded-2xl object-cover border-2 border-amber-200" />
              : <div className="h-20 w-20 rounded-2xl bg-amber-50 border-2 border-dashed border-amber-200 flex items-center justify-center">
                  <Building2 className="h-8 w-8 text-amber-300" />
                </div>
            }
            {logoUploading && (
              <div className="absolute inset-0 rounded-2xl bg-white/80 flex items-center justify-center">
                <div className="animate-spin h-5 w-5 border-2 border-amber-500 border-t-transparent rounded-full" />
              </div>
            )}
          </div>
          <div>
            <input ref={logoRef} type="file" accept="image/*" className="hidden"
              onChange={e => e.target.files?.[0] && handleLogoUpload(e.target.files[0])} />
            <Button size="sm" variant="outline" onClick={() => logoRef.current?.click()} disabled={logoUploading}
              className="rounded-xl border-amber-200 text-amber-700 hover:bg-amber-50">
              <Camera className="h-4 w-4 mr-2" />{tr ? 'Logo Yükle' : 'Upload Logo'}
            </Button>
            <p className="text-xs text-gray-400 mt-1.5">{tr ? 'JPG, PNG, WebP · Maks. 5 MB' : 'JPG, PNG, WebP · Max 5 MB'}</p>
          </div>
        </div>
      </div>

      {/* ── Company Info ── */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 space-y-5">
        <p className="text-sm font-semibold text-gray-700">{tr ? 'Firma Bilgileri' : 'Company Information'}</p>
        <div className="grid grid-cols-2 gap-4">

          <FIELD label={tr ? 'Firma Adı' : 'Company Name'} icon={Building2}>
            <Input value={form.company_name || ''} onChange={e => set('company_name', e.target.value)} className="rounded-xl" />
          </FIELD>

          <FIELD label={tr ? 'Yetkili Kişi' : 'Contact Person'}>
            <Input value={form.contact_person_name || ''} onChange={e => set('contact_person_name', e.target.value)} className="rounded-xl" />
          </FIELD>

          <FIELD label="Email" icon={Mail}>
            <Input value={form.email || ''} disabled className="rounded-xl bg-gray-50 text-gray-400 cursor-not-allowed" />
          </FIELD>

          <FIELD label={tr ? 'Telefon' : 'Phone'}>
            <PhoneInput
              codeValue={phoneCode}
              numberValue={phoneNumber}
              onCodeChange={setPhoneCode}
              onNumberChange={(v) => { setPhoneNumber(v); if (!phoneTouched && v) setPhoneTouched(true); }}
            />
            {phoneTouched && phoneNumber && !phoneValid && (
              <p className="text-xs text-red-500 flex items-center gap-1 mt-1">
                <AlertCircle className="h-3 w-3" />
                {tr ? 'Telefon 7–15 rakam içermelidir' : 'Phone must be 7–15 digits'}
              </p>
            )}
          </FIELD>

          <FIELD label={tr ? 'Website' : 'Website'} icon={Globe}>
            <Input
              type="url"
              inputMode="url"
              value={form.website || ''}
              onChange={e => set('website', e.target.value.trim())}
              onBlur={() => setWebsiteTouched(true)}
              className={`rounded-xl ${websiteTouched && form.website && !websiteValid ? 'border-red-400' : ''}`}
              placeholder="https://firma.com"
              autoComplete="url"
            />
            {websiteTouched && form.website && !websiteValid && (
              <p className="text-xs text-red-500 flex items-center gap-1 mt-1">
                <AlertCircle className="h-3 w-3" />
                {tr ? 'Geçerli bir site giriniz (örn. firma.com)' : 'Enter a valid site (e.g. company.com)'}
              </p>
            )}
          </FIELD>

          <FIELD label={tr ? 'Ülke' : 'Country'} icon={MapPin}>
            <div className="relative">
              <select
                value={form.country || ''}
                onChange={e => set('country', e.target.value)}
                className={SELECT_CLS}
              >
                <option value="">{tr ? '-- Ülke Seçin --' : '-- Select Country --'}</option>
                {UN_COUNTRIES_EN.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              <ChevronDown className="absolute right-3 top-2.5 h-4 w-4 text-gray-400 pointer-events-none" />
            </div>
          </FIELD>

          <FIELD label={tr ? 'Şehir' : 'City'}>
            <Input value={form.city || ''} onChange={e => set('city', e.target.value)} className="rounded-xl" />
          </FIELD>

          <FIELD label={tr ? 'Posta Kodu' : 'Zip Code'}>
            <Input value={form.zip_code || ''} onChange={e => set('zip_code', e.target.value)} className="rounded-xl" placeholder="34000" maxLength={10} />
          </FIELD>

          <FIELD label={tr ? 'Adres' : 'Address'} span2>
            <textarea
              rows={3}
              value={form.address || ''}
              onChange={e => set('address', e.target.value)}
              className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300 resize-y"
              placeholder={tr ? 'Sokak, mahalle, bina bilgisi...' : 'Street, neighborhood, building...'}
            />
          </FIELD>
        </div>

        {/* Descriptions */}
        <FIELD label={tr ? 'Kısa Tanıtım' : 'Short Description'}>
          <textarea
            rows={3}
            value={form.short_description || ''}
            onChange={e => set('short_description', e.target.value)}
            className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300 resize-y min-h-[80px]"
            placeholder={tr ? 'Firmanızı kısaca tanıtın...' : 'Brief company introduction...'}
          />
        </FIELD>
        <FIELD label={tr ? 'Detaylı Tanıtım' : 'Full Description'}>
          <textarea
            rows={5}
            value={form.full_description || ''}
            onChange={e => set('full_description', e.target.value)}
            className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300 resize-y min-h-[120px]"
            placeholder={tr ? 'Detaylı firma açıklaması, hizmetler, referanslar...' : 'Detailed description, services, references...'}
          />
        </FIELD>

        <div className="grid grid-cols-2 gap-4">
          <FIELD label={tr ? 'Desteklenen Diller (virgülle)' : 'Supported Languages (comma-sep.)'} icon={Languages}>
            <Input
              value={(form.supported_languages || []).join(', ')}
              onChange={e => set('supported_languages', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
              className="rounded-xl" placeholder="EN, TR, AR"
            />
          </FIELD>
          <FIELD label={tr ? 'Hizmet Bölgeleri (virgülle)' : 'Service Regions (comma-sep.)'}>
            <Input
              value={(form.service_regions || []).join(', ')}
              onChange={e => set('service_regions', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
              className="rounded-xl" placeholder="Türkiye, Orta Doğu, Avrupa"
            />
          </FIELD>
        </div>
      </div>

      {/* ── Legal / Documents Section ── */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 space-y-4">
        <p className="text-sm font-semibold text-gray-700">{tr ? 'Yasal Bilgiler' : 'Legal Information'}</p>

        {isTurkish ? (
          /* Turkish company fields */
          <div className="grid grid-cols-2 gap-4">
            <FIELD label={tr ? 'Şirket Resmi Ünvanı' : 'Official Company Title'} icon={Building2} span2>
              <Input value={form.official_company_title || ''} onChange={e => set('official_company_title', e.target.value)} className="rounded-xl" placeholder={tr ? 'Tam resmi ünvan...' : 'Full official title...'} />
            </FIELD>
            <FIELD label={tr ? 'Vergi Dairesi' : 'Tax Office'} icon={FileText}>
              <Input value={form.tax_office || ''} onChange={e => set('tax_office', e.target.value)} className="rounded-xl" placeholder={tr ? 'Örn: Kadıköy V.D.' : 'e.g. Kadıköy Tax Office'} />
            </FIELD>
            <FIELD label={tr ? 'Vergi No' : 'Tax Number'}>
              <Input value={form.tax_no || ''} onChange={e => set('tax_no', e.target.value)} className="rounded-xl" placeholder="1234567890" maxLength={11} />
            </FIELD>
            <FIELD label={tr ? 'Ticaret Sicil Numarası' : 'Trade Registry Number'} icon={FileText} span2>
              <Input value={form.trade_registry_no || ''} onChange={e => set('trade_registry_no', e.target.value)} className="rounded-xl" placeholder={tr ? 'Ticaret sicil numarası...' : 'Trade registry number...'} />
            </FIELD>
          </div>
        ) : (
          /* Non-Turkish company fields */
          <div className="grid grid-cols-2 gap-4">
            <FIELD label={tr ? 'Ticaret Lisansı No' : 'Trade License No.'} icon={FileText}>
              <Input value={form.trade_license_number || ''} onChange={e => set('trade_license_number', e.target.value)} className="rounded-xl" />
            </FIELD>
            <FIELD label={tr ? 'Vergi / Sicil No' : 'Tax / Reg. No.'}>
              <Input value={form.tax_or_registration_number || ''} onChange={e => set('tax_or_registration_number', e.target.value)} className="rounded-xl" />
            </FIELD>
          </div>
        )}
      </div>

      <Button onClick={handleSave} disabled={saving} data-testid="profile-save-btn"
        className="bg-amber-500 hover:bg-amber-600 text-white rounded-xl px-8 py-2.5 font-semibold">
        <Save className="h-4 w-4 mr-2" />
        {saving ? (tr ? 'Kaydediliyor...' : 'Saving...') : (tr ? 'Kaydet' : 'Save Changes')}
      </Button>
    </div>
  );
}
